import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { Users, CardsThree, Lightning, CurrencyDollar, ChartLine, MagnifyingGlass, Crown, Shield, Plus, Trash, Envelope, Sparkle, FolderOpen, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [invites, setInvites] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("admin");
  const [auditLog, setAuditLog] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [promoTier, setPromoTier] = useState("plus");
  const [promoDays, setPromoDays] = useState(30);
  const [promoMaxUses, setPromoMaxUses] = useState(100);
  // Collapsible sections — default OPEN for Users (existing workflow), but one click folds.
  // Persisted in localStorage so admins don't have to re-collapse every load.
  const [openUsers, setOpenUsers] = useState(() => {
    try { return localStorage.getItem("ekkol_admin_users_open") !== "0"; } catch { return true; }
  });
  const toggleUsers = () => setOpenUsers((v) => {
    const next = !v;
    try { localStorage.setItem("ekkol_admin_users_open", next ? "1" : "0"); } catch {}
    return next;
  });
  // Bulk-select state for "Assign plan to N users"
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [bulkPlan, setBulkPlan] = useState("plus");
  const [bulkDays, setBulkDays] = useState(30);
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelected = (uid) => setSelectedUserIds((prev) => {
    const n = new Set(prev);
    if (n.has(uid)) n.delete(uid); else n.add(uid);
    return n;
  });
  const selectAllVisible = (checked) => setSelectedUserIds((prev) => {
    const n = new Set(checked ? [] : prev);
    if (checked) users.forEach((u) => n.add(u.user_id));
    return n;
  });
  const clearSelection = () => setSelectedUserIds(new Set());
  const bulkAssignPlan = async () => {
    const ids = Array.from(selectedUserIds);
    if (!ids.length) return;
    const planLabel = bulkPlan.toUpperCase();
    const when = Number(bulkDays) === 0 ? "permanently" : `for ${bulkDays} day${Number(bulkDays) === 1 ? "" : "s"}`;
    toast(`Grant ${planLabel} ${when} to ${ids.length} user${ids.length === 1 ? "" : "s"}?`, {
      duration: 10000,
      action: {
        label: "Confirm",
        onClick: async () => {
          setBulkBusy(true);
          try {
            const { data } = await api.post("/admin/users/bulk-assign-plan", {
              user_ids: ids, plan: bulkPlan, days: Number(bulkDays) || 0,
            });
            toast.success(`Updated ${data.modified} user${data.modified === 1 ? "" : "s"} → ${planLabel}`);
            // Refresh the visible list
            try {
              const u = await api.get("/admin/users?limit=50");
              setUsers(u.data.users); setTotal(u.data.total);
            } catch {}
            clearSelection();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk assign failed");
          } finally { setBulkBusy(false); }
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  const loadPromos = async () => {
    try {
      const { data } = await api.get("/admin/promo-codes");
      setPromoCodes(data.codes || data.items || []);
    } catch {}
  };

  const createPromo = async () => {
    if (!promoCode.trim()) { toast.error("Code required"); return; }
    try {
      await api.post("/admin/promo-codes", {
        code: promoCode.trim().toUpperCase(),
        tier: promoTier,
        duration_days: Number(promoDays) || 30,
        max_uses: Number(promoMaxUses) || 100,
      });
      toast.success("Promo code created");
      setPromoCode("");
      await loadPromos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    }
  };

  const disablePromo = async (code) => {
    try {
      await api.delete(`/admin/promo-codes/${encodeURIComponent(code)}`);
      toast.success("Disabled");
      await loadPromos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const copyPromo = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  useEffect(() => {
    if (loading) return;
    if (!user?.is_admin) return;
    (async () => {
      try {
        const [a, b, c] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/ai-usage?days=14"),
          api.get("/admin/users?limit=50"),
        ]);
        setStats(a.data);
        setUsage(b.data);
        setUsers(c.data.users);
        setTotal(c.data.total);
        if (user.is_super) {
          try {
            const inv = await api.get("/admin/invites");
            setInvites(inv.data.invites || []);
          } catch {}
        }
        try {
          const audit = await api.get("/admin/audit?limit=50");
          setAuditLog(audit.data.items || []);
        } catch {}
        try {
          await loadPromos();
        } catch {}
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load admin data");
      } finally { setBusy(false); }
    })();
  }, [loading, user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[color:var(--paper)]"><Navbar/>
        <main className="max-w-2xl mx-auto px-6 py-20 text-center">
          <Shield size={40} className="mx-auto mb-4 text-[color:var(--ink-muted)]"/>
          <h1 className="font-serif text-4xl">Admin only</h1>
          <p className="text-[color:var(--ink-muted)] mt-4">Your account doesn&apos;t have admin access. Contact the owner to be whitelisted in <code className="font-mono bg-[color:var(--surface-alt)] px-2 py-0.5">ADMIN_EMAILS</code>.</p>
          <Link to="/dashboard" className="btn-primary mt-8 inline-flex">Back to library</Link>
        </main>
      </div>
    );
  }

  const searchUsers = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/users?limit=50&q=${encodeURIComponent(q)}`);
      setUsers(data.users); setTotal(data.total);
    } catch {} finally { setBusy(false); }
  };

  const updateUser = async (uid, patch) => {
    try {
      await api.patch(`/admin/users/${uid}`, patch);
      setUsers((us) => us.map((u) => u.user_id === uid ? { ...u, ...patch } : u));
      toast.success("User updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const addInvite = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      await api.post("/admin/invites", { email, role: newRole });
      toast.success(`${email} invited as ${newRole}`);
      const inv = await api.get("/admin/invites");
      setInvites(inv.data.invites || []);
      setNewEmail("");
      setNewRole("admin");
      // Refresh users in case they already existed
      const u = await api.get("/admin/users?limit=50");
      setUsers(u.data.users); setTotal(u.data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
    }
  };

  const removeInvite = async (email) => {
    if (!window.confirm(`Revoke admin access for ${email}?`)) return;
    try {
      await api.delete(`/admin/invites/${encodeURIComponent(email)}`);
      toast.success(`Revoked ${email}`);
      setInvites((arr) => arr.filter((x) => x.email !== email));
      const u = await api.get("/admin/users?limit=50");
      setUsers(u.data.users); setTotal(u.data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  };

  const manageThrottle = async (u) => {
    const { data } = await api.get(`/admin/users/${u.user_id}/usage`);
    const current = data.override || {};
    const matchCap = prompt(
      `Daily AI match cap for ${u.email}\n\nPlan default: ${data.effective_caps.match ?? "unlimited"}\nUsed today: ${data.used_today.match || 0}\n\nEnter a number to override, empty to clear override, or "unlimited":`,
      current.match ?? ""
    );
    if (matchCap === null) return;
    try {
      if (matchCap === "") {
        await api.delete(`/admin/users/${u.user_id}/throttle`);
        toast.success("Override cleared");
      } else {
        const val = matchCap === "unlimited" ? -1 : parseInt(matchCap, 10);
        if (Number.isNaN(val)) { toast.error("Not a number"); return; }
        await api.post(`/admin/users/${u.user_id}/throttle`, { match: val });
        toast.success("Throttle saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12 fade-in">
        <div className="flex items-center gap-3 mb-4">
          <Crown size={22} className="text-[color:var(--oxford)]"/>
          <span className="eyebrow">Admin console</span>
          {user.is_super && (
            <span className="eyebrow text-[color:var(--vermilion)] border border-[color:var(--vermilion)] px-2 py-0.5">
              Super admin
            </span>
          )}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none">Operations</h1>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/admin/vault" className="btn-ghost inline-flex" data-testid="btn-vault">
            <FolderOpen size={14}/> Document vault
          </Link>
        </div>

        {err && <div className="mt-6 border rule bg-white p-4 text-[color:var(--vermilion)] text-sm">{err}</div>}

        {/* Sticky jump nav — one tap to any major section, essential once the user list gets long */}
        <nav
          className="mt-6 -mx-6 md:-mx-10 px-6 md:px-10 py-3 border-y rule bg-[color:var(--paper)]/90 backdrop-blur-sm sticky top-16 z-20 flex items-center gap-1 overflow-x-auto"
          data-testid="admin-jumpnav"
        >
          <span className="eyebrow text-[color:var(--ink-muted)] mr-2 shrink-0">Jump to</span>
          <a href="#section-users" className="text-xs link-underline whitespace-nowrap px-2 py-1" data-testid="jump-users">Users</a>
          <span className="text-[color:var(--ink-faint)]">·</span>
          <a href="#section-ai" className="text-xs link-underline whitespace-nowrap px-2 py-1" data-testid="jump-ai">AI usage</a>
          {user.is_super && (
            <>
              <span className="text-[color:var(--ink-faint)]">·</span>
              <a href="#section-access" className="text-xs link-underline whitespace-nowrap px-2 py-1" data-testid="jump-access">Admin access</a>
            </>
          )}
          <span className="text-[color:var(--ink-faint)]">·</span>
          <a href="#section-promo" className="text-xs link-underline whitespace-nowrap px-2 py-1" data-testid="jump-promo">Promo codes</a>
          <span className="text-[color:var(--ink-faint)]">·</span>
          <a href="#section-audit" className="text-xs link-underline whitespace-nowrap px-2 py-1" data-testid="jump-audit">Audit log</a>
          <span className="text-[color:var(--ink-faint)]">·</span>
          <Link to="/admin/vault" className="text-xs link-underline whitespace-nowrap px-2 py-1" data-testid="jump-vault">Vault</Link>
        </nav>

        {/* Top metrics */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--rule)] border rule" data-testid="admin-stats">
          <MetricCard icon={<Users size={18}/>} label="Users" value={stats?.users.total ?? "—"} sub={`+${stats?.users.signups_this_week ?? 0} this week`}/>
          <MetricCard icon={<ChartLine size={18}/>} label="Active 7d" value={stats?.users.active_this_week ?? "—"} sub={`${stats?.activity.attempts_week ?? 0} attempts/week`}/>
          <MetricCard icon={<CardsThree size={18}/>} label="Decks" value={stats?.content.decks_total ?? "—"} sub={`${stats?.content.cards_total ?? 0} cards`}/>
          <MetricCard icon={<Lightning size={18}/>} label="AI month" value={stats?.ai.calls_month ?? "—"} sub={`$${stats?.ai.est_cost_month_usd?.toFixed(2) ?? "0.00"} est.`}/>
        </div>

        <div className="mt-px grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--rule)] border rule border-t-0">
          <MetricCard icon={<CurrencyDollar size={18}/>} label="MRR" value={`$${stats?.revenue.mrr_usd?.toFixed(2) ?? "0.00"}`} sub={`ARR $${stats?.revenue.arr_usd?.toFixed(2) ?? "0.00"}`}/>
          <MetricCard icon={<Crown size={18}/>} label="Paying" value={stats?.revenue.paying_customers ?? 0} sub="paid users"/>
          <MetricCard icon={<CardsThree size={18}/>} label="Public decks" value={stats?.content.decks_public ?? 0} sub="shared"/>
          <MetricCard icon={<ChartLine size={18}/>} label="Today" value={stats?.activity.attempts_today ?? 0} sub="attempts today"/>
        </div>

        <p className="mt-3 text-xs font-mono text-[color:var(--ink-muted)]">
          AI cost is an estimate at {`$${stats?.ai?.est_cost_month_usd !== undefined ? "≈ $0.0005" : "~"}`}/call for GPT-4.1-mini. Real billing lives with your Emergent Universal Key balance.
        </p>

        {/* AI usage */}
        <div className="mt-12 border rule bg-white" id="section-ai" style={{ scrollMarginTop: 120 }}>
          <div className="p-6 border-b rule flex items-center justify-between">
            <h2 className="font-serif text-2xl">AI usage (last 14 days)</h2>
            <span className="font-mono text-xs text-[color:var(--ink-muted)]">
              {usage?.by_endpoint.reduce((s, r) => s + r.calls, 0) ?? 0} calls · ${(usage?.by_endpoint.reduce((s, r) => s + r.cost_usd, 0) ?? 0).toFixed(2)}
            </span>
          </div>
          <div className="p-6">
            {usage?.by_endpoint?.length ? (
              <div className="space-y-2" data-testid="ai-endpoints">
                {usage.by_endpoint.map((e) => {
                  const maxCalls = Math.max(...usage.by_endpoint.map((x) => x.calls), 1);
                  const pct = (e.calls / maxCalls) * 100;
                  return (
                    <div key={e.endpoint} className="flex items-center gap-3">
                      <div className="font-mono text-xs uppercase tracking-widest w-40 truncate">{e.endpoint}</div>
                      <div className="flex-1 h-2 bg-[color:var(--surface-alt)] relative">
                        <div className="absolute inset-y-0 left-0 bg-[color:var(--ink)]" style={{width: `${pct}%`}}/>
                      </div>
                      <div className="font-mono text-xs w-20 text-right">{e.calls}</div>
                      <div className="font-mono text-xs w-20 text-right text-[color:var(--ink-muted)]">${e.cost_usd.toFixed(3)}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-[color:var(--ink-muted)]">No AI calls yet.</div>
            )}
          </div>
        </div>

        {/* Users */}
        <div className="mt-12 border rule bg-white" id="section-users" style={{ scrollMarginTop: 120 }}>
          <button
            type="button"
            onClick={toggleUsers}
            className="w-full p-6 border-b rule flex items-center justify-between flex-wrap gap-3 hover:bg-[color:var(--surface-alt)] transition-colors text-left"
            data-testid="admin-users-toggle"
            aria-expanded={openUsers}
          >
            <h2 className="font-serif text-2xl flex items-center gap-3">
              <Users size={20} className="text-[color:var(--oxford)]"/>
              Users
              <span className="font-mono text-sm text-[color:var(--ink-muted)]">{total} total</span>
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[color:var(--ink-muted)] hidden sm:inline">{openUsers ? "Click to collapse" : "Click to expand"}</span>
              <CaretDown size={16} className={`text-[color:var(--ink-muted)] transition-transform ${openUsers ? "rotate-180" : ""}`}/>
            </div>
          </button>
          {openUsers && (
            <>
              <div className="px-6 py-4 border-b rule flex items-center justify-end gap-2 flex-wrap">
                <MagnifyingGlass size={14} className="text-[color:var(--ink-muted)]"/>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchUsers()}
                  placeholder="Search email or name…"
                  className="bg-transparent border-b rule px-2 py-1 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="admin-user-search"
                />
                <button onClick={searchUsers} className="btn-ghost py-1.5 px-3 text-xs">Search</button>
              </div>

              {/* Bulk-assign action bar — only visible when 1+ rows are checked */}
              {selectedUserIds.size > 0 && (
                <div
                  className="px-6 py-3 border-b rule bg-[color:var(--oxford)]/5 flex items-center justify-between gap-3 flex-wrap sticky top-[calc(var(--jumpnav-h,48px)+64px)] z-10"
                  data-testid="bulk-actions-bar"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--oxford)]">
                      {selectedUserIds.size} selected
                    </span>
                    <button onClick={clearSelection} className="text-xs link-underline text-[color:var(--ink-muted)] ml-1" data-testid="bulk-clear">Clear</button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-[color:var(--ink-muted)]">Plan</label>
                    <select
                      value={bulkPlan}
                      onChange={(e) => setBulkPlan(e.target.value)}
                      className="bg-transparent border rule px-2 py-1 text-xs font-mono uppercase"
                      data-testid="bulk-plan-select"
                    >
                      <option value="plus">PLUS</option>
                      <option value="edu">EDU</option>
                      <option value="free">FREE (downgrade)</option>
                    </select>
                    <label className="text-xs text-[color:var(--ink-muted)] ml-2">Duration</label>
                    <select
                      value={bulkDays}
                      onChange={(e) => setBulkDays(Number(e.target.value))}
                      className="bg-transparent border rule px-2 py-1 text-xs font-mono"
                      data-testid="bulk-days-select"
                      disabled={bulkPlan === "free"}
                    >
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                      <option value={180}>180 days</option>
                      <option value={365}>365 days</option>
                      <option value={0}>Permanent (no expiry)</option>
                    </select>
                    <button
                      onClick={bulkAssignPlan}
                      disabled={bulkBusy}
                      className="btn-primary py-1.5 px-3 text-xs"
                      data-testid="bulk-assign-btn"
                    >
                      <Sparkle size={12} weight="fill"/> {bulkBusy ? "Assigning…" : "Assign plan"}
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto" data-testid="admin-users-table">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="text-left border-b rule">
                      <th className="p-4 w-10">
                        <input
                          type="checkbox"
                          checked={users.length > 0 && users.every((u) => selectedUserIds.has(u.user_id))}
                          onChange={(e) => selectAllVisible(e.target.checked)}
                          className="accent-[color:var(--ink)]"
                          aria-label="Select all visible users"
                          data-testid="bulk-select-all"
                        />
                      </th>
                      <th className="p-4 font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">User</th>
                      <th className="p-4 font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">Plan</th>
                      <th className="p-4 font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">Decks</th>
                      <th className="p-4 font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">Activity 7d</th>
                      <th className="p-4 font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">Joined</th>
                      <th className="p-4 font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">Actions</th>
                    </tr>
                  </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className={`border-b rule last:border-0 ${selectedUserIds.has(u.user_id) ? "bg-[color:var(--oxford)]/5" : ""}`}>
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(u.user_id)}
                        onChange={() => toggleSelected(u.user_id)}
                        className="accent-[color:var(--ink)]"
                        aria-label={`Select ${u.email}`}
                        data-testid={`bulk-select-${u.user_id}`}
                      />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {u.picture && <img src={u.picture} alt="" className="w-8 h-8 rounded-full border rule"/>}
                        <div>
                          <div className="font-medium">{u.name || "—"}</div>
                          <div className="font-mono text-xs text-[color:var(--ink-muted)]">{u.email}</div>
                        </div>
                        {u.is_super ? (
                          <span className="eyebrow text-[color:var(--vermilion)] border border-[color:var(--vermilion)] px-1.5 py-0.5" title="Super admin">Super</span>
                        ) : u.is_admin ? (
                          <Shield size={14} className="text-[color:var(--oxford)]" title="Admin"/>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-4">
                      <select
                        value={u.plan || "free"}
                        onChange={(e) => updateUser(u.user_id, { plan: e.target.value })}
                        className="bg-transparent border rule px-2 py-1 text-xs font-mono uppercase"
                      >
                        <option value="free">FREE</option>
                        <option value="plus">PLUS</option>
                        <option value="edu">EDU</option>
                        {u.plan === "team" && <option value="team">TEAM (legacy)</option>}
                      </select>
                    </td>
                    <td className="p-4 font-mono">{u.decks}</td>
                    <td className="p-4 font-mono">{u.attempts_this_week}</td>
                    <td className="p-4 font-mono text-xs text-[color:var(--ink-muted)]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="p-4">
                      {user.is_super ? (
                        <div className="flex flex-col items-start gap-1">
                          <button
                            onClick={() => updateUser(u.user_id, { is_admin: !u.is_admin })}
                            className="text-xs link-underline"
                          >
                            {u.is_admin ? "Revoke admin" : "Grant admin"}
                          </button>
                          {!u.is_super ? (
                            <button
                              onClick={() => updateUser(u.user_id, { is_super: true, is_admin: true })}
                              className="text-xs link-underline text-[color:var(--vermilion)]"
                            >Make super</button>
                          ) : u.user_id !== user.user_id ? (
                            <button
                              onClick={() => updateUser(u.user_id, { is_super: false })}
                              className="text-xs link-underline text-[color:var(--ink-muted)]"
                            >Demote super</button>
                          ) : (
                            <span className="text-xs text-[color:var(--ink-muted)]">(you)</span>
                          )}
                          <button
                            onClick={() => manageThrottle(u)}
                            className="text-xs link-underline text-[color:var(--ink-muted)]"
                            data-testid={`throttle-${u.user_id}`}
                          >Throttle…</button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-start gap-1">
                          <button
                            onClick={() => manageThrottle(u)}
                            className="text-xs link-underline"
                            data-testid={`throttle-${u.user_id}`}
                          >Throttle…</button>
                          <span className="text-xs text-[color:var(--ink-faint)]">Super only: role changes</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!users.length && !busy && (
                  <tr><td colSpan={7} className="p-10 text-center text-sm text-[color:var(--ink-muted)]">No users match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
            </>
          )}
        </div>

        {/* Admin access (super only) */}
        {user.is_super && (
          <div className="mt-12 border rule bg-white" data-testid="admin-access-section" id="section-access" style={{ scrollMarginTop: 120 }}>
            <div className="p-6 border-b rule flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 text-[color:var(--vermilion)]">
                  <Crown size={18}/><span className="eyebrow">Super admin</span>
                </div>
                <h2 className="font-serif text-2xl mt-1">Admin access</h2>
                <p className="text-xs text-[color:var(--ink-muted)] mt-1">
                  Pre-authorize new admins by email. They&apos;ll be elevated automatically the moment they sign in with Google.
                </p>
              </div>
            </div>
            <div className="p-6 border-b rule">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[240px]">
                  <label className="eyebrow block mb-2">Email to invite</label>
                  <div className="flex items-center gap-2 border rule bg-white px-3 py-2">
                    <Envelope size={14} className="text-[color:var(--ink-muted)]"/>
                    <input
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addInvite()}
                      placeholder="name@company.com"
                      className="flex-1 bg-transparent focus:outline-none text-sm"
                      data-testid="invite-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Role</label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    className="bg-white border rule px-3 py-2 text-sm font-mono uppercase"
                    data-testid="invite-role"
                  >
                    <option value="admin">ADMIN</option>
                    <option value="super">SUPER</option>
                  </select>
                </div>
                <button onClick={addInvite} className="btn-primary py-2.5 px-4 text-sm" data-testid="invite-add">
                  <Plus size={14}/> Add
                </button>
              </div>
            </div>
            <div className="p-6">
              {invites.length === 0 ? (
                <div className="text-sm text-[color:var(--ink-muted)]">No invites yet.</div>
              ) : (
                <div className="space-y-2" data-testid="invites-list">
                  {invites.map((inv) => (
                    <div key={inv.email} className="flex items-center justify-between border rule p-3">
                      <div>
                        <div className="font-mono text-sm">{inv.email}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)] mt-1">
                          Role: {inv.role} · {inv.claimed_at ? `CLAIMED ${new Date(inv.claimed_at).toLocaleDateString()}` : "PENDING SIGN-IN"}
                          {inv.added_by && <> · by {inv.added_by}</>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {inv.role === "super" && <span className="eyebrow text-[color:var(--vermilion)] border border-[color:var(--vermilion)] px-1.5 py-0.5">Super</span>}
                        <button
                          onClick={() => removeInvite(inv.email)}
                          className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)]"
                          aria-label={`Revoke ${inv.email}`}
                          data-testid={`invite-revoke-${inv.email}`}
                        >
                          <Trash size={16}/>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Promo codes */}
        <section className="mt-16" data-testid="admin-promo-section" id="section-promo" style={{ scrollMarginTop: 120 }}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-3xl">Promo codes</h2>
            <span className="font-mono text-xs text-[color:var(--ink-muted)]">{promoCodes.length} total</span>
          </div>

          {user.is_super && (
            <div className="border rule bg-white p-5 mb-6 grid md:grid-cols-5 gap-3 items-end">
              <label className="block md:col-span-2">
                <div className="eyebrow mb-1">Code</div>
                <input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="SCHOLAR50"
                  className="w-full bg-transparent border rule px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="promo-new-code"
                />
              </label>
              <label className="block">
                <div className="eyebrow mb-1">Tier</div>
                <select
                  value={promoTier}
                  onChange={(e) => setPromoTier(e.target.value)}
                  className="w-full bg-white border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="promo-new-tier"
                >
                  <option value="plus">Plus</option>
                  <option value="edu">Edu</option>
                </select>
              </label>
              <label className="block">
                <div className="eyebrow mb-1">Days</div>
                <input type="number" min="1" value={promoDays} onChange={(e) => setPromoDays(e.target.value)}
                  className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="promo-new-days"/>
              </label>
              <label className="block">
                <div className="eyebrow mb-1">Max uses</div>
                <input type="number" min="1" value={promoMaxUses} onChange={(e) => setPromoMaxUses(e.target.value)}
                  className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="promo-new-max-uses"/>
              </label>
              <button
                onClick={createPromo}
                className="bg-[color:var(--ink)] text-white px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[color:var(--ink-2)] md:col-span-5"
                data-testid="promo-create-btn"
              >
                <Plus size={12} className="inline mr-1"/> Create promo code
              </button>
            </div>
          )}

          <div className="border rule bg-white divide-y rule" data-testid="promo-list">
            {promoCodes.length === 0 ? (
              <div className="p-6 text-sm text-[color:var(--ink-muted)] text-center">
                No promo codes yet. {user.is_super ? "Create one above to get started." : "Super admin can create codes."}
              </div>
            ) : (
              promoCodes.map((p) => (
                <div key={p.code} className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`promo-row-${p.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-base font-semibold tracking-wider">{p.code}</div>
                    <div className="text-xs text-[color:var(--ink-muted)] font-mono mt-1">
                      {p.tier?.toUpperCase()} · {p.duration_days || "∞"} days · Used {p.uses_count || 0}/{p.max_uses || "∞"} · {p.is_active === false ? "Disabled" : "Active"}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => copyPromo(p.code)}
                      className="text-xs font-mono uppercase tracking-widest border rule px-3 py-1.5 hover:bg-[color:var(--surface-alt)]"
                      data-testid={`promo-copy-${p.code}`}
                    >Copy</button>
                    {user.is_super && p.is_active !== false && (
                      <button
                        onClick={() => disablePromo(p.code)}
                        className="text-xs font-mono uppercase tracking-widest border border-[color:var(--vermilion)] text-[color:var(--vermilion)] px-3 py-1.5 hover:bg-[color:var(--vermilion)] hover:text-white"
                        data-testid={`promo-disable-${p.code}`}
                      >Disable</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <p className="text-[11px] text-[color:var(--ink-muted)] mt-3 font-mono">
            Share redemption URL: <span className="text-[color:var(--ink)]">ekkol.app/profile#promo</span> — users paste the code there.
          </p>
        </section>

        {/* Audit log */}
        <div className="mt-12 border rule bg-white" id="section-audit" style={{ scrollMarginTop: 120 }}>
          <div className="p-6 border-b rule flex items-center justify-between">
            <h2 className="font-serif text-2xl">Audit log</h2>
            <span className="font-mono text-xs text-[color:var(--ink-muted)]">last {auditLog.length} actions</span>
          </div>
          <div className="divide-y rule max-h-96 overflow-auto" data-testid="audit-log">
            {auditLog.length === 0 ? (
              <div className="p-10 text-center text-sm text-[color:var(--ink-muted)]">No admin actions yet.</div>
            ) : (
              auditLog.map((a, i) => (
                <div key={i} className="p-4 flex items-start gap-4">
                  <div className="font-mono text-xs text-[color:var(--ink-muted)] w-36 shrink-0">
                    {a.at ? new Date(a.at).toLocaleString() : "—"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--oxford)]">{a.action}</span>
                      {a.target && <span className="ml-3 font-mono text-xs text-[color:var(--ink-muted)]">→ {a.target}</span>}
                    </div>
                    <div className="text-xs text-[color:var(--ink-muted)] mt-0.5">
                      by <span className="font-mono">{a.actor_email}</span>
                      {a.details && Object.keys(a.details).length > 0 && (
                        <span className="ml-2 font-mono text-[color:var(--ink-faint)]">{JSON.stringify(a.details)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Teacher / Classroom placeholder */}
        <div className="mt-12 border rule bg-white p-8">
          <div className="eyebrow mb-3">Coming in Team / Edu tier</div>
          <h2 className="font-serif text-2xl">Classroom, lesson plans, and roster</h2>
          <p className="text-[color:var(--ink-muted)] mt-3 max-w-2xl">
            Once the Plus subscription ships, the Team tier will enable: shared deck libraries per
            classroom, lesson-plan assignments with due dates, roster management (import CSV or
            Google Classroom), and per-student accuracy/time dashboards. Scaffolding for this is in
            the data model (deck sharing, public links). Ask me to build Phase 1 of this whenever
            you&apos;re ready.
          </p>
        </div>
      </main>
    </div>
  );
}

function MetricCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white p-5">
      <div className="flex items-center gap-2 text-[color:var(--ink-muted)]">
        {icon}
        <span className="eyebrow">{label}</span>
      </div>
      <div className="font-mono text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-[color:var(--ink-muted)] mt-1">{sub}</div>}
    </div>
  );
}
